import { ClearLimitIcon } from "@/assets";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthState } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import {
  clearUserLoginLimit,
  ExemptBanks,
  RequireBanks,
  SuspendPayout,
  unSuspendPayout,
  verifyEmail,
} from "@/services/Users.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, CircleCheck } from "lucide-react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaFileShield } from "react-icons/fa6";
import { HiOutlineMail } from "react-icons/hi";

const UserActions = ({
  id,
  userExempt,
  payoutLocked,
  kycStatus,
}: {
  id: string;
  userExempt: boolean;
  payoutLocked: boolean;
  kycStatus: string;
}) => {
  const queryClient = useQueryClient();
  const { state } = useAuthState();

  const mutationClearLimit = useMutation({
    mutationFn: async (payload) => await clearUserLoginLimit(payload),
    mutationKey: ["clearlimit"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer login limit cleared");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationVerify = useMutation({
    mutationFn: async () => await verifyEmail(id),
    mutationKey: ["verifyEmail"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer email verified");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationExempt = useMutation({
    mutationFn: async () => await ExemptBanks(id),
    mutationKey: ["exempt"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer banks kyc exempted successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationRequire = useMutation({
    mutationFn: async () => await RequireBanks(id),
    mutationKey: ["require"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer banks kyc required successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationSuspend = useMutation({
    mutationFn: async () => await SuspendPayout(id),
    mutationKey: ["suspendPayout"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer payout suspended successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationUnsuspend = useMutation({
    mutationFn: async () => await unSuspendPayout(id),
    mutationKey: ["unsuspendPayout"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer payout unsuspended successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const handleClearLimit = () => {
    mutationClearLimit.mutate({ userId: id });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="px-4 py-2 ">
          <span className="sr-only">Open menu</span>
          <BsThreeDotsVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(state?.merchant?.roleId === "merchant-owner" ||
          state?.merchant?.roleId === "merchant-admin") && (
          <DropdownMenuItem
            onClick={handleClearLimit}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <ClearLimitIcon />
            Clear Login Limit
          </DropdownMenuItem>
        )}

        {kycStatus === "unverified" && (
          <DropdownMenuItem
            onClick={() => mutationVerify.mutate()}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <HiOutlineMail />
            Verify Email
          </DropdownMenuItem>
        )}

        {userExempt ? (
          <DropdownMenuItem
            onClick={() => mutationRequire.mutate()}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <FaFileShield />
            Remove Exemption
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => mutationExempt.mutate()}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <FaFileShield />
            Exempt KYC
          </DropdownMenuItem>
        )}

        {payoutLocked ? (
          <DropdownMenuItem
            onClick={() => mutationUnsuspend.mutate()}
            className="text-green-3 flex items-center gap-2 text-xs"
          >
            <CircleCheck />
            Unsuspend payout
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => mutationSuspend.mutate()}
            className="text-error flex items-center gap-2 text-xs "
          >
            <Ban />
            Suspend payout
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserActions;
